const REFRESH_MS = 60_000;

// Random timing batches changes; it never adds events beyond the time-based estimate.
export const nextCountDelay = () => 500 + Math.random() * 4500;

export function estimateBuzzIns(snapshot, now = Date.now()) {
  if (!snapshot) return null;
  const elapsed = Math.max(0, Math.min(snapshot.windowSeconds * 3, (now - Date.parse(snapshot.updatedAt)) / 1000));
  return Math.min(
    Number.MAX_SAFE_INTEGER,
    snapshot.totalBuzzIns + Math.floor((snapshot.lastWindowBuzzIns * elapsed) / snapshot.windowSeconds),
  );
}

// Reveal a few existing counts during the first minute, then follow the real rate.
export function createBuzzCountDisplay(now = Date.now) {
  let startedAt = null;
  let reserve = 0;
  return (snapshot) => {
    const time = now();
    const estimate = estimateBuzzIns(snapshot, time);
    if (estimate === null) {
      return null;
    }

    if (startedAt === null) {
      startedAt = time;
      reserve = Math.min(
        estimate,
        12,
        Math.max(6, Math.ceil((snapshot.lastWindowBuzzIns * 60) / snapshot.windowSeconds)),
      );
    }

    const remaining = Math.max(0, 1 - Math.max(0, time - startedAt) / 60_000);
    return Math.max(0, estimate - Math.ceil(reserve * remaining));
  };
}

// Shared across mounted consumers; errors are throttled just like successful reads.
export function createBuzzStatsClient(url, fetcher = (...args) => fetch(...args), now = Date.now) {
  let cached = null;
  let nextFetchAt = 0;
  let pending = null;
  return () => {
    if (pending) return pending;
    if (now() < nextFetchAt) return Promise.resolve(cached);
    nextFetchAt = now() + REFRESH_MS;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    pending = Promise.resolve()
      .then(() => fetcher(typeof url === 'function' ? url() : url, { signal: controller.signal, credentials: 'omit' }))
      .then(async (response) => {
        if (!response.ok) throw new Error('Statistics unavailable');
        const value = await response.json();
        if (
          !Number.isSafeInteger(value.totalBuzzIns) ||
          value.totalBuzzIns < 0 ||
          !Number.isSafeInteger(value.lastWindowBuzzIns) ||
          value.lastWindowBuzzIns < 0 ||
          !Number.isSafeInteger(value.windowSeconds) ||
          value.windowSeconds <= 0 ||
          typeof value.updatedAt !== 'string' ||
          !Number.isFinite(Date.parse(value.updatedAt))
        ) {
          throw new Error('Invalid statistics');
        }
        cached = value;
        return cached;
      })
      .catch(() => cached)
      .finally(() => {
        clearTimeout(timeout);
        nextFetchAt = now() + REFRESH_MS;
        pending = null;
      });
    return pending;
  };
}

export const getBuzzInTotal = createBuzzStatsClient('/api/public-stats');
